import {
  AuthCheck,
  AuthLayout,
  AuthUserPermission,
  Can,
  FailedToLoadContent,
  Loading,
  useAuth,
  useSelfOrgContent,
  useWindowTitle,
} from '@cibo/profile'
import { logNonRequestError } from '@cibo/ui'
import * as Sentry from '@sentry/react'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { Analytics } from 'components/Analytics'
import { useSetAppMeta } from 'hooks'
import { TranslationPreviewer } from 'i18n/TranslationPreviewer'
import 'mapbox-gl/dist/mapbox-gl.css'
import { Suspense, lazy, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Config } from 'types/Config'
import { useOrgDialect } from './i18n'

const AuthenticatedArea = lazy(() => import('./pages/AuthenticatedArea'))
const LoggedOutRoutes = lazy(() => import('./pages/LoggedOutRoutes'))

const ReactQueryDevtoolsProduction = lazy(() =>
  import('@tanstack/react-query-devtools').then(d => ({
    default: d.ReactQueryDevtools,
  }))
)

export const ImpactApp = () => {
  useWindowTitle()
  const { authResolved, isLoggedIn, userId, org: organization } = useAuth()

  const navigate = useNavigate()
  const [deepLink, setDeepLink] = useState<string>()

  const [showDevtools, setShowDevtools] = useState(false)

  useEffect(() => {
    // @ts-ignore toggleDevTools isn't natively included in window, but exists here
    window.toggleDevtools = () => setShowDevtools(old => !old)
  }, [])

  useEffect(() => {
    Sentry.setUser(isLoggedIn ? { id: userId?.toString() } : null)
  }, [isLoggedIn, userId])

  useEffect(() => {
    if (authResolved && isLoggedIn && deepLink) {
      setDeepLink(undefined)
      navigate(deepLink, { replace: true })
    }
  }, [authResolved, deepLink, isLoggedIn])
  const homepageIsSet = !!Config.get('homepage')

  useOrgDialect()
  const orgContent = useSelfOrgContent()
  useSetAppMeta()

  const [orgContentProblem, setOrgContentProblem] = useState(false)
  useEffect(() => {
    if (isLoggedIn && (!organization?.contentfulOrgId || orgContent.isError)) {
      setOrgContentProblem(true)
      if (!organization?.contentfulOrgId) {
        logNonRequestError(new Error('user has no org content id'), {
          organization,
          location: 'missing contentful id',
        })
      }
      if (orgContent.isError) {
        logNonRequestError(new Error('error fetching org content'), {
          orgContent,
          location: 'org content error',
        })
      }
    } else {
      setOrgContentProblem(false)
    }
  }, [isLoggedIn, organization, orgContent.isError])

  const appLoading = !authResolved || (organization?.contentfulOrgId && orgContent.isFetching)

  return (
    <>
      <AuthCheck />

      {orgContentProblem ? (
        <AuthLayout variant="content">
          <FailedToLoadContent />
        </AuthLayout>
      ) : appLoading ? (
        <Loading large />
      ) : !isLoggedIn ? (
        <Suspense fallback={<Loading large />}>
          <LoggedOutRoutes />
        </Suspense>
      ) : (
        <Suspense fallback={<Loading large />}>
          <AuthenticatedArea />
        </Suspense>
      )}

      <Can useAny={[AuthUserPermission.TRANSLATIONS_VIEW, AuthUserPermission.DEBUG_USER]}>
        <TranslationPreviewer />
      </Can>
      <Can useAny={AuthUserPermission.DEBUG_USER}>
        <ReactQueryDevtools buttonPosition="bottom-left" />
      </Can>
      {showDevtools && (
        <Suspense fallback={null}>
          <ReactQueryDevtoolsProduction />
        </Suspense>
      )}
      {homepageIsSet && <Analytics />}
    </>
  )
}
